package dev.lingvo.site.pages.app.projects.slug.data

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.shared.MtJobDto
import dev.lingvo.shared.MtJobState
import dev.lingvo.shared.MtJobsDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LFilesIcon
import dev.lingvo.site.ds.LIconAction
import dev.lingvo.site.ds.LKeyValueRowLinkStyle
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LPauseIcon
import dev.lingvo.site.ds.LRefreshIcon
import dev.lingvo.site.ds.LSpinner
import dev.lingvo.site.ds.LSpinnerSize
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableHeaderScope
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.LTimeStyle
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.LTooltip
import dev.lingvo.site.ds.LWandSparklesIcon
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TOAST_UNTIL_DISMISSED
import dev.lingvo.site.ds.ToastHost
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.formatUtc
import dev.lingvo.site.ds.rememberToasts
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectAction
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

/**
 * Machine-translation runs in this project.
 *
 * **Who started a run is absent for translators and viewers, not blanked out.** Who spends the
 * workspace's money is a management fact; a translator seeing "Anna, 4,000 credits" learns something
 * about a colleague's budget that has nothing to do with translating. The server leaves the name out
 * of the response entirely — a name that reaches the browser has been disclosed whatever the page
 * then does with it — so this page shows "Automation" where it has nothing.
 *
 * Reserved and refunded stay apart for the same reason they do on every other money surface: a run
 * that failed and gave its credits back did not cost nothing, and it did not cost full price either.
 *
 * **Refresh keeps the table.** `loadAsync` re-keyed on a counter, so pressing Refresh replaced the
 * list with skeleton bars and destroyed the button that had just been pressed. `loadReloadable`
 * reloads underneath: the rows stay, and a refresh that fails leaves the last good list up.
 */
@Page("/app/projects/{slug}/data/jobs")
@Layout(".components.layouts.AppLayout")
@Composable
fun ProjectJobsPage() {
    // Kobweb's processor refuses a `@Page` that takes anything but a `PageContext`, so the seam
    // lives one function down — the same shape `PretranslateContent` and `GlossaryContent` already
    // use, and for the same reason: the route reads the slug, the content does the work.
    ProjectJobsContent(slug = rememberPageContext().route.params["slug"].orEmpty())
}

/**
 * The runs table and everything that acts on it.
 *
 * @param loadJobs how the run list is fetched — this screen's one seam, and the only one it needs.
 * Everything else here is already reachable from a mounted page: the claim comes from the live
 * route, the toasts from the host, the router from the page context. What could not be reached was
 * the transport, and the behaviour worth protecting — that a refresh reloads *underneath* the table
 * rather than replacing it with skeleton bars — is only observable across two answers from it. The
 * default is what production runs.
 */
@Composable
internal fun ProjectJobsContent(
    slug: String,
    loadJobs: suspend (String) -> MtJobsDto = { projectSlug -> ApiClient.mtJobs(projectSlug) },
) {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val toasts = rememberToasts()
    var refreshKey by remember { mutableStateOf(0) }
    var stopping by remember { mutableStateOf<String?>(null) }
    val reloadable = loadReloadable(slug, reloadToken = refreshKey) { loadJobs(slug) }
    // Starting, repeating and stopping runs share the exact project's spend authority. Reading
    // the list stays available to every project role.
    val spendClaim = ProjectActionClaim.capture(
        liveProjectRoute(slug),
        Session.organizations,
        ProjectPrivilege.SPEND,
    )

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(
            t("app.projects.slug.data.jobs.translation_runs", "Translation runs"),
            t("app.projects.slug.data.jobs.every_pre_translate_in_this_project", "Every pre-translate in this project, and what it cost."),
            actions = {
                LIconAction(
                    label = t("app.projects.slug.data.jobs.refresh_the_runs", "Refresh the runs"),
                    onClick = { refreshKey++ },
                    loading = reloadable.refreshing,
                    icon = { LRefreshIcon() },
                )
            },
        )

        reloadable.refreshError?.let { failure ->
            LAlert(
                title = t("app.projects.slug.data.jobs.showing_the_last_list_that_loaded", "Showing the last list that loaded"),
                description = failure.message,
                modifier = Modifier.fillMaxWidth(),
                variant = LAlertVariant.WARNING,
                live = true,
                dense = true,
            )
        }

        LAsyncContent(
            reloadable.state,
            rows = 4,
            loadingLabel = t("app.projects.slug.data.jobs.loading_runs", "Loading runs"),
            onRetry = { refreshKey++ },
        ) { loaded ->
            // The content slot is a single element, so the column that spaced the table, the notes
            // under it and the links apart has to be inside it.
            Column(Modifier.fillMaxWidth().gap(Space.XL)) {
                val jobs = loaded.jobs
                if (jobs.isEmpty()) {
                    LEmptyState(
                        t("app.projects.slug.data.jobs.no_runs_yet", "No runs yet"),
                        t("app.projects.slug.data.jobs.pre_translate_fills_empty_strings_from_2", "Pre-translate fills empty strings from a machine provider. Nothing here has " +
                            "used one — which also means nothing here has cost credits."),
                        Modifier.fillMaxWidth(),
                        LEmptyStateIcon.REFRESH,
                    ) {
                        if (spendClaim != null) {
                            LButton(
                                t("app.projects.slug.data.jobs.start_a_run", "Start a run"),
                                { ctx.router.tryRoutingTo("/app/projects/$slug/pretranslate") },
                                leadingIcon = { LWandSparklesIcon() },
                            )
                        }
                    }
                } else {
                    LTable(caption = t("app.projects.slug.data.jobs.machine_translation_runs", "Translation runs")) {
                        LTableHead {
                            LTableRow {
                                LTableHeaderCell { Text(t("app.projects.slug.data.jobs.started", "Started")) }
                                LTableHeaderCell { Text(t("app.projects.slug.data.jobs.who", "Who")) }
                                LTableHeaderCell { Text(t("app.projects.slug.data.jobs.provider", "Provider")) }
                                LTableHeaderCell { Text(t("app.projects.slug.data.jobs.languages", "Languages")) }
                                LTableHeaderCell { Text(t("app.projects.slug.data.jobs.strings", "Strings")) }
                                LTableHeaderCell { Text(t("app.projects.slug.data.jobs.credits", "Credits")) }
                                LTableHeaderCell { Text(t("app.projects.slug.data.jobs.outcome", "Outcome")) }
                                LTableHeaderCell { Text(t("app.projects.slug.data.jobs.actions", "Actions")) }
                            }
                        }
                        LTableBody {
                            jobs.forEach { row ->
                                val job = row.job
                                LTableRow {
                                    LTableCell {
                                        // Through the product's inline-link style, with Silk's own
                                        // `:link` paint switched off: a bare `Link` here renders in
                                        // the user-agent blue that appears in neither palette.
                                        Link(
                                            path = "/app/projects/$slug/data/jobs/${job.id}",
                                            modifier = LKeyValueRowLinkStyle.toModifier(),
                                            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                                        ) {
                                            // Through `formatUtc`, which also names the zone —
                                            // a bare "2026-09-02 14:03" beside a UTC-month ledger
                                            // is a guess.
                                            Text(formatUtc(job.createdAt, LTimeStyle.MINUTE))
                                        }
                                    }
                                    // "Automation" covers both a run nobody started and a run whose
                                    // actor this reader is not shown. Both mean the same thing here:
                                    // there is no person to ask about it.
                                    LTableCell {
                                        Text(row.actor ?: t("app.projects.slug.data.jobs.automation", "Automation"))
                                    }
                                    LTableHeaderCell(scope = LTableHeaderScope.ROW) {
                                        Text(job.provider.displayName)
                                    }
                                    LTableCell {
                                        Text(job.locales.joinToString(", ").ifBlank { "—" })
                                        // What the run was for, where the row has room for it: a
                                        // retry beside its original otherwise reads as two
                                        // unrelated runs over the same locale minutes apart.
                                        job.intentWords()?.let { LMuted(it) }
                                    }
                                    LTableCell {
                                        P(Modifier.tabularNumbers().margin(0.px).toAttrs()) {
                                            Text(tf(
                                                "app.projects.slug.data.jobs.done_of_total",
                                                "{done} of {total}",
                                                "done" to formatCount(job.done),
                                                "total" to formatCount(job.total),
                                            ))
                                        }
                                    }
                                    LTableCell { CreditsCell(job) }
                                    LTableCell { OutcomeBadge(job) }
                                    LTableCell {
                                        Row(Modifier.gap(Space.XS)) {
                                            if (spendClaim != null && job.state == MtJobState.RUNNING && !job.abandoned) {
                                                LIconAction(
                                                    label = trf(
                                                        "app.projects.slug.data.jobs.stop_the_run_started_at",
                                                        "Stop the run started at {when}",
                                                        "when" to formatUtc(job.createdAt, LTimeStyle.MINUTE),
                                                    ),
                                                    onClick = {
                                                        val claim = spendClaim
                                                        scope.launch {
                                                            stopping = job.id
                                                            try {
                                                                requestProjectAction(
                                                                    claim = claim,
                                                                    currentRoute = { liveProjectRoute(slug) },
                                                                    currentMemberships = { Session.organizations },
                                                                ) { ApiClient.cancelMtJob(slug, job.id) }
                                                                toasts.show(
                                                                    tr("app.projects.slug.data.jobs.run_stopped", "Run stopped"),
                                                                    LToastVariant.NEUTRAL,
                                                                )
                                                                refreshKey++
                                                            } catch (cancelled: CancellationException) {
                                                                throw cancelled
                                                            } catch (failure: Throwable) {
                                                                toasts.show(
                                                                    failure.safeJobMessage(),
                                                                    LToastVariant.DANGER,
                                                                    TOAST_UNTIL_DISMISSED,
                                                                )
                                                            } finally {
                                                                stopping = null
                                                            }
                                                        }
                                                    },
                                                    size = LButtonSize.SM,
                                                    loading = stopping == job.id,
                                                    icon = { LPauseIcon() },
                                                )
                                            }
                                            if (spendClaim != null && job.state != MtJobState.RUNNING) {
                                                // A deep link rather than an immediate re-run: the
                                                // pre-translate screen prices it first, which is
                                                // what "run again" has to mean for something that
                                                // spends money.
                                                LIconAction(
                                                    label = trf(
                                                        "app.projects.slug.data.jobs.run_this_again_in",
                                                        "Run this again in {languages}",
                                                        "languages" to job.locales.joinToString(", ")
                                                            .ifBlank {
                                                                tr(
                                                                    "app.projects.slug.data.jobs.the_same_languages",
                                                                    "the same languages",
                                                                )
                                                            },
                                                    ),
                                                    onClick = {
                                                        ctx.router.tryRoutingTo(
                                                            "/app/projects/$slug/pretranslate" +
                                                                "?provider=${job.provider.id}" +
                                                                "&locales=${job.locales.joinToString(",")}" +
                                                                "&onlyEmpty=${job.onlyEmpty}",
                                                        )
                                                    },
                                                    size = LButtonSize.SM,
                                                    icon = { LRefreshIcon() },
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    // **`MtJobDto.error` is never rendered** (`docs/design/run-receipt.md` §14.2).
                    // It is a raw provider blob and a provider error body can echo the key that was
                    // submitted, so a run that failed says so with its badge and sends the reader to
                    // the receipt for the reason.
                    if (jobs.any { it.job.state == MtJobState.FAILED }) {
                        LMuted(t("app.projects.slug.data.jobs.open_a_failed_run_to_see", "Open a failed run to see what stopped it and what it cost."))
                    }
                    LMuted(
                        t("app.projects.slug.data.jobs.runs_on_your_own_provider_key_2", "Runs on your own provider key cost zero credits and still appear here — the " +
                            "strings were still translated by a machine."),
                    )
                }
                // **Buttons, not bare anchors.** These two were `Link`s with no variant, so Silk
                // painted them in its default link colour — measured #0000FF on paper and #00FFFF
                // on ink, a hue that appears in neither palette and fails the 4.5:1 text floor in
                // Day. Everything that is not the accent is greyscale, so a ghost pair is what a
                // secondary way out of this page looks like. "Start a run" is dropped when the
                // list is empty, because the empty state above already invites exactly that and a
                // screen must not offer the same action twice at two different weights.
                Row(Modifier.gap(Space.MD).flexWrap(FlexWrap.Wrap)) {
                    if (jobs.isNotEmpty()) {
                        LLinkButton(
                            t("app.projects.slug.data.jobs.start_a_run", "Start a run"),
                            "/app/projects/$slug/pretranslate",
                            variant = LButtonVariant.GHOST,
                            size = LButtonSize.SM,
                            leadingIcon = { LWandSparklesIcon() },
                        )
                    }
                    LLinkButton(
                        t("app.projects.slug.data.jobs.project_data", "Project data"),
                        "/app/projects/$slug/data",
                        variant = LButtonVariant.GHOST,
                        size = LButtonSize.SM,
                        leadingIcon = { LFilesIcon() },
                    )
                }
            }
        }
    }
    ToastHost(toasts)
}

/**
 * The reservation, and whether any of it came back.
 *
 * The figure is the *reservation*, and saying "936, refunded" of it claims all 936 came back. The
 * reaper returns only the undelivered proportion, so the cell reports that a refund happened and
 * leaves the arithmetic to the receipt — with the explanation on the tooltip rather than as a
 * standing paragraph under a table most readers never need it for.
 */
@Composable
private fun CreditsCell(job: MtJobDto) {
    if (job.reservedCredits == 0) {
        P(Modifier.tabularNumbers().margin(0.px).toAttrs()) { Text("0") }
        return
    }
    if (job.refundedAt == null) {
        P(Modifier.tabularNumbers().margin(0.px).toAttrs()) { Text(formatCount(job.reservedCredits)) }
        return
    }
    LTooltip(
        t("app.projects.slug.data.jobs.a_refunded_run_had_credits_returned_2", "A refunded run had credits returned for the strings it did not translate. The " +
            "reservation stays visible because \"it cost nothing\" and \"it was never charged\" are " +
            "different sentences on an invoice."),
    ) { trigger ->
        P(trigger.tabularNumbers().margin(0.px).toAttrs()) {
            Text(
                trf(
                    "app.projects.slug.data.jobs.reserved_refund_issued",
                    "{credits} reserved, refund issued",
                    "credits" to formatCount(job.reservedCredits),
                ),
            )
        }
    }
}

/**
 * The outcome, in words, with a spinner while it is still happening.
 *
 * `abandoned` is read before the state, because a job killed with the process stays RUNNING for ever
 * while its credits are already back.
 */
@Composable
private fun OutcomeBadge(job: MtJobDto) {
    when {
        job.cancelled -> LBadge(
            t("app.projects.slug.data.jobs.stopped", "Stopped"),
            variant = LBadgeVariant.NEUTRAL,
            dot = true,
        )
        job.abandoned -> LBadge(
            t("app.projects.slug.data.jobs.interrupted", "Interrupted"),
            variant = LBadgeVariant.WARNING,
            dot = true,
        )
        job.state == MtJobState.DONE -> LBadge(
            t("app.projects.slug.data.jobs.done", "Done"),
            variant = LBadgeVariant.SUCCESS,
            dot = true,
        )
        job.state == MtJobState.RUNNING -> LBadge(
            t("app.projects.slug.data.jobs.running", "Running"),
            variant = LBadgeVariant.INFO,
            leading = { LSpinner(size = LSpinnerSize.SM) },
        )
        else -> LBadge(
            t("app.projects.slug.data.jobs.failed", "Failed"),
            variant = LBadgeVariant.DANGER,
            dot = true,
        )
    }
}

/** "Gaps only" or "rewrite", plus the tone, when the run recorded one. Null when it says nothing. */
private fun MtJobDto.intentWords(): String? {
    val coverage = if (onlyEmpty) {
        tr("app.projects.slug.data.jobs.gaps_only", "gaps only")
    } else {
        tr("app.projects.slug.data.jobs.rewrite", "rewrite")
    }
    val words = listOfNotNull(
        coverage,
        tone,
        retryOf?.let { tr("app.projects.slug.data.jobs.retry", "retry") },
    )
    return words.joinToString(" · ")
}

private fun Throwable.safeJobMessage(): String =
    (this as? ApiException)?.message?.takeIf(String::isNotBlank)
        ?: tr(
            "app.projects.slug.data.jobs.couldnt_stop_this_run",
            "Couldn't stop this run. It may have finished on its own.",
        )
